/**
 * Email Verification Page
 * 
 * Handles email verification when user clicks the link from their email.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Logo from '../Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided.');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email?token=${token}`);
      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || 'Email verified successfully!');
        
        // If user is logged in, update their local state
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const user = JSON.parse(storedUser);
            user.email_verified = true;
            localStorage.setItem('user', JSON.stringify(user));
          } catch (e) {
            // Ignore parse errors
          }
        }
      } else {
        setStatus('error');
        setMessage(data.detail || 'Verification failed. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('An error occurred. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-8 text-center">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Logo size="large" />
          </div>

          {/* Status Icon */}
          <div className="mb-6">
            {status === 'verifying' && (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            )}
            {status === 'error' && (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {status === 'verifying' && 'Verifying Email...'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
          </h1>

          {/* Message */}
          <p className="text-gray-600 mb-6">
            {status === 'verifying' && 'Please wait while we verify your email address.'}
            {status === 'success' && message}
            {status === 'error' && message}
          </p>

          {/* Actions */}
          {status === 'success' && (
            <div className="space-y-3">
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate('/consumer/dashboard')}
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
              <p className="text-sm text-gray-500">
                You can now enjoy all DealShaq features!
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-3">
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate('/consumer/login')}
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
              <p className="text-sm text-gray-500">
                Log in to request a new verification email.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
